import { nextTick } from 'vue'
import { debugConsoleError, debugConsoleWarn } from '@/modules/chat-matrix-fast/utils/chatDebugLog'

/** Pins the chat log to the latest message without a smooth animation. */
export function scrollChatLogToBottom(scrollEl: HTMLElement | null | undefined): boolean {
  if (!scrollEl) {
    return false
  }

  scrollEl.style.scrollBehavior = 'auto'
  scrollEl.scrollTop = scrollEl.scrollHeight - scrollEl.clientHeight
  return true
}

/** Double-rAF + height retry — лента часто дорастает после первого кадра. */
export function scheduleScrollChatLogToBottom(scrollEl: HTMLElement | null | undefined): void {
  if (!scrollEl) {
    return
  }

  try {
    const performScroll = () => {
      const scrollHeight = scrollEl.scrollHeight
      scrollChatLogToBottom(scrollEl)

      window.setTimeout(() => {
        if (scrollEl.scrollHeight > scrollHeight) {
          scrollChatLogToBottom(scrollEl)
        }
      }, 100)
    }

    requestAnimationFrame(() => {
      performScroll()
      requestAnimationFrame(() => {
        performScroll()
      })
    })
  } catch (error) {
    debugConsoleError(error)
  }
}

/** Scroll chat log container to a message and briefly highlight it. */
export async function scrollToChatMessage(
  scrollContainer: HTMLElement | null | undefined,
  messageIndex: number | string,
  matrixEventId?: string,
  options?: {
    refetch?: () => Promise<void>
    /** Deep history: Matrix /context around event id, then re-render. */
    ensureEvent?: (eventId: string) => Promise<void>
  },
): Promise<boolean> {
  if (!scrollContainer) {
    debugConsoleWarn('[scrollToChatMessage] scroll container missing')
    return false
  }

  try {
    await nextTick()
    await new Promise(resolve => setTimeout(resolve, 50))

    const chatLogByClass = scrollContainer.querySelector('.chat-log')
    const chatLogContainer = chatLogByClass || scrollContainer

    const findMessage = (): HTMLElement | null => {
      const messages = chatLogContainer.querySelectorAll(
        '[data-message-index], [data-matrix-event-id]',
      )

      return Array.from(messages).find(el => {
        const element = el as HTMLElement
        const index = element.dataset.messageIndex || ''
        const eventId = element.dataset.matrixEventId || ''
        const target = String(messageIndex)

        return target === String(index)
          || (eventId && (eventId === target || eventId === matrixEventId))
      }) as HTMLElement | null
    }

    let targetMessage = findMessage()

    const eventIdForJump
      = (typeof matrixEventId === 'string' && matrixEventId.startsWith('$') && matrixEventId)
      || (typeof messageIndex === 'string' && messageIndex.startsWith('$') && messageIndex)
      || undefined

    if (!targetMessage && eventIdForJump && options?.ensureEvent) {
      await options.ensureEvent(eventIdForJump)
      await nextTick()
      await new Promise(resolve => setTimeout(resolve, 200))
      targetMessage = findMessage()
    }

    if (!targetMessage && options?.refetch) {
      await options.refetch()
      await nextTick()
      await new Promise(resolve => setTimeout(resolve, 200))
      targetMessage = findMessage()
    }

    if (!targetMessage) {
      return false
    }

    const highlightRect = targetMessage.getBoundingClientRect()
    const containerRect = scrollContainer.getBoundingClientRect()
    const messageTop = highlightRect.top - containerRect.top + scrollContainer.scrollTop
    const targetScrollTop
      = messageTop - scrollContainer.clientHeight / 2 + targetMessage.offsetHeight / 2
    const maxScrollTop = scrollContainer.scrollHeight - scrollContainer.clientHeight

    scrollContainer.scrollTop = Math.max(0, Math.min(targetScrollTop, maxScrollTop))
    await nextTick()

    targetMessage.classList.add('message-highlight')
    window.setTimeout(() => {
      targetMessage?.classList.remove('message-highlight')
    }, 2000)

    return true
  } catch (error) {
    debugConsoleError(error)
    return false
  }
}
